import { assistedAffiliationClient } from "./assistedAffiliationClient";
import { getAssistedAffiliationHost } from "./getAssistedAffiliationHost";
import {
  ApiEnvelope,
  AssistedAffiliationDraft,
  AssistedAffiliationDraftObservation,
  AssistedAffiliationDraftProduct,
} from "./types";

// Espeja al endpoint de creación con otro verbo: la identificación viaja en el body, no en la ruta.
const assistedAffiliationDraftEndpoint = "/v1/assisted-affiliation/draft";

// El servicio exige el precio como número y la consulta puede devolverlo como texto ("0.00"): se
// convierte solo cuando el texto es un número, para no inventar un 0 a partir de un campo vacío.
const withNumericPrice = (
  product: AssistedAffiliationDraftProduct | null | undefined
) => {
  const price: unknown = product?.price;
  if (!product || typeof price !== "string" || !price.trim()) return product;

  const numericPrice = Number(price);
  return Number.isFinite(numericPrice) ? { ...product, price: numericPrice } : product;
};

/**
 * Guarda los cambios de una solicitud existente.
 * @param draft - El registro completo como se consultó, con lo editado encima: se manda todo, no solo
 * lo editado. El servicio escribe lo que recibe y deja intacto lo que no llega.
 * @param observations - Solo las notas NUEVAS de esta edición. El servicio agrega al historial todo
 * lo que recibe, así que las que el registro ya traía de la consulta no se reenvían nunca: se
 * duplicarían.
 * @returns El contenido de `data`, que el servicio devuelve vacío: solo confirma la operación, no
 * responde el registro. Relanza el error para que la pantalla lo traduzca.
 */
export const updateAssistedAffiliationDraftService = async (
  draft: AssistedAffiliationDraft,
  observations: AssistedAffiliationDraftObservation[] = []
): Promise<AssistedAffiliationDraft | null> => {
  const host = await getAssistedAffiliationHost();
  const { observations: _loadedObservations, ...record } = draft;

  const response = await assistedAffiliationClient.put<
    ApiEnvelope<AssistedAffiliationDraft | null>
  >(`${host}${assistedAffiliationDraftEndpoint}`, {
    ...record,
    product: withNumericPrice(record.product),
    observations,
  });

  return response.data.data ?? null;
};
